import type { AllCategories, ShowedCategories } from '@cc-shopping/contracts/compare/schema/types';
import { entitiesResponseSchema } from 'src/app/api/credit-cards/og/marketplace-search/schema';
import * as v from 'valibot';

export const allowedCategories: AllCategories[] = [
  'airline-credit-cards',
  'bad-credit-cards',
  'balance-transfer-credit-cards',
  'business-credit-cards',
  'cash-rewards-credit-cards',
  'credit-card-bonus-offers',
  'gas-credit-cards',
  'low-apr-credit-cards',
  'low-interest-credit-cards',
  'rewards-credit-cards',
  'secured-credit-cards',
  'student-credit-cards',
  'travel-miles-credit-cards',
  'zero-percent-credit-cards',
];

export const categoryLabelMapper: Partial<Record<AllCategories, string>> = {
  'airline-credit-cards': 'Airline',
  'bad-credit-cards': 'Bad Credit',
  'balance-transfer-credit-cards': 'Balance Transfer',
  'business-credit-cards': 'Business',
  'cash-rewards-credit-cards': 'Cash Back',
  'credit-card-bonus-offers': 'Bonus Offers',
  'gas-credit-cards': 'Gas',
  'low-apr-credit-cards': 'Low Interest',
  'low-interest-credit-cards': 'Low Interest',
  'rewards-credit-cards': 'Rewards',
  'secured-credit-cards': 'Secured',
  'student-credit-cards': 'Student',
  'travel-miles-credit-cards': 'Travel',
  'zero-percent-credit-cards': '0% Intro APR',
};

export const categorySlugMapper: Record<
  Exclude<ShowedCategories, 'the-best-credit-cards'>,
  string
> = {
  'balance-transfer-credit-cards': 'balance-transfer',
  'cash-rewards-credit-cards': 'cash-back',
  'rewards-credit-cards': 'rewards',
  'travel-miles-credit-cards': 'travel',
  'zero-percent-credit-cards': 'low-interest',
};

export async function fetchAllEntities(): Promise<v.InferOutput<typeof entitiesResponseSchema>> {
  try {
    const response = await fetch('/api/credit-cards/og/marketplace-search/get-all-entities', {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });
    const resp = (await response.json()) as { data: unknown };
    const entities = v.parse(entitiesResponseSchema, resp);
    return entities;
  } catch (error) {
    if (typeof window === 'undefined') {
      import('@cc-shopping/platform-logger').then(mod => {
        const { loggerFactory } = mod;
        const logger = loggerFactory({
          path: 'apps/web/src/app/credit-cards/compare/components/Context/utils.ts',
          component: 'fetchAllEntities',
        });
        logger.error({ error }, 'Failed to fetch all entities');
      });
    } else {
      import('@datadog/browser-rum').then(mod => {
        const { datadogRum } = mod;
        datadogRum.addError(error, { message: 'Failed to fetch all entities' });
      });
    }
    return [];
  }
}

export const fetchEntitiesByIds = async ({
  ids,
  legacyIds,
}: {
  ids: string[];
  legacyIds: string[];
}) => {
  try {
    const response = await fetch(
      `/api/credit-cards/og/marketplace-search/get-entities-by-id?ids=${ids.join(
        ',',
      )}&legacyIds=${legacyIds.join(',')}`,
      {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      },
    );
    const resp = (await response.json()) as { data: unknown };
    const entities = v.parse(entitiesResponseSchema, resp);
    return entities;
  } catch (error) {
    if (typeof window === 'undefined') {
      import('@cc-shopping/platform-logger').then(mod => {
        const { loggerFactory } = mod;
        const logger = loggerFactory({
          path: 'apps/web/src/app/credit-cards/compare/components/Context/utils.ts',
          component: 'fetchEntitiesByIds',
        });
        logger.error({ error, ids, legacyIds }, 'Failed to fetch entities by ids');
      });
    } else {
      import('@datadog/browser-rum').then(mod => {
        const { datadogRum } = mod;
        datadogRum.addError(error, { message: 'Failed to fetch entities by ids' });
      });
    }
    return [];
  }
};

export const DEFAULT_APPLY_NOW_MIN_HEIGHT = 69;
export const DEFAULT_STICKY_HEADER_MIN_HEIGHT_DESKTOP = 280;
export const DEFAULT_STICKY_HEADER_MIN_HEIGHT_MOBILE = 240;
export const LOCAL_STORAGE_KEYS_MAP = {
  TOOLS_PAGES: 'nw_compare',
  PREVIOUS_IDS: 'prevIds',
  IDS_TO_COMPARE: 'compareIds',
};

export const isValidJson = (value: string): boolean => {
  try {
    JSON.parse(value);
    return true;
  } catch {
    return false;
  }
};

export const getIdsFromQueryParams = (): string | null => {
  const searchParams = typeof window !== 'undefined' ? window.location.search : '';
  const queryParams = new URLSearchParams(searchParams);
  return queryParams.get('compareIds');
};
